import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Divider, Form, Spin } from 'antd';
import { cloneDeep } from 'es-toolkit';

import { useGetDefaultCustomerSettings } from '@/redux/hooks/useCustomerSettings';

import {
  deleteShipmentAddressBookInProgress,
  searchCustomerAddressBookInProgress,
  searchCustomerAddressBookInSuccess,
  setCustomerAddressBookInError,
  setShipmentAddressBookFilter,
} from '../../../../redux/shipment/ShipmentActions';
import { valueToString } from '../../../../utilz';
import { routeFileSelector } from '../../../../utilz/i18n';
import EmptyRecords from '../../../components/Common/EmptyRecords';
import CommonModal from '../../../components/Modals/CommonModal';
import ScrollPagination from '../../../components/Paginations/ScrollPagination';
import AddLocationForm from './AddLocationForm';
import AddressBookCard from './AddressBookCard';

const AddressBookModal = ({ addressBookModal, handleAddressBookModalClose, onSelectAddressBook }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const saveAddressBookProgressing = useSelector((state) => state.shipment.saveAddressBook.progressing, shallowEqual);
  const addressBookFilters = useSelector((state) => state.shipment.addressBookFilters, shallowEqual);
  const customerAddressBooks = useSelector((state) => state.shipment.customerAddressBooks, shallowEqual);
  const selectedCompany = useSelector((state) => state.auth.selectedCompany, shallowEqual);

  const { data: defaultSettings } = useGetDefaultCustomerSettings(
    selectedCompany?.companyId,
    'Shipment',
    'DefaultCountryCode',
  );

  const deleteAddressBookProgressing = useSelector(
    (state) => state.shipment.deleteAddressBook.progressing,
    shallowEqual,
  );
  const userId = useSelector((state) => state.auth.user.data?.id, shallowEqual);

  const [isSearchShow, setIsSearchShow] = useState(false);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleOnClickSearch = () => {
    const formData = form.getFieldsValue();

    const generateRange = (from, to) => {
      if (!from && !to) {
        return '';
      }
      return `${from || ''}-${to || ''}`;
    };
    const filterData = {
      address: valueToString(formData?.addr1),
      appointmentType: formData?.appointmentType,
      city: valueToString(formData?.city),
      contactEmail: formData?.contactEmail,
      contactName: formData?.contactName,
      countryCallingCode: formData?.countryCallingCode,
      countryCode: valueToString(formData?.countryCode),
      customerKey: valueToString(selectedCompany?.companyId),
      dataReset: true,
      daysOfWeek: generateRange(formData?.fromOperationDay, formData?.toOperationDay),
      hoursOfOperation: generateRange(
        formData?.fromOperationHours?.format('hh:mm A'),
        formData?.toOperationHours?.format('hh:mm A'),
      ),
      name: valueToString(formData?.name),
      pageNumber: 1,
      pageSize: 10,
      phone: formData?.phone,
      phoneExt: formData?.phoneExt,
      searchId: valueToString(formData?.searchId),
      siteId: formData?.siteId,
      state: valueToString(formData?.state),
      zip: valueToString(formData?.zip),
    };
    dispatch(setShipmentAddressBookFilter(filterData));
    dispatch(searchCustomerAddressBookInProgress());
    setIsSearchShow(true);
  };

  const handleDeleteSuccess = (index) => {
    const items = cloneDeep(customerAddressBooks?.data);
    items.splice(index, 1);
    dispatch(setCustomerAddressBookInError({ data: items }));
  };

  const handleDelete = (data, index) => {
    const formData = {
      callBackSuccess: () => handleDeleteSuccess(index),
      id: data?.locationId,
    };
    dispatch(deleteShipmentAddressBookInProgress(formData));
  };

  const handleNextPage = () => {
    dispatch(
      setShipmentAddressBookFilter({
        ...addressBookFilters,
        pageNumber: Number(addressBookFilters?.pageNumber) + 1,
      }),
    );
    dispatch(searchCustomerAddressBookInProgress());
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    if (!addressBookModal) {
      form.resetFields();
      dispatch(searchCustomerAddressBookInSuccess({ data: [] }));
      setIsSearchShow(false);
    }
  }, [addressBookModal]);

  return (
    <CommonModal
      title={t('translation:default.fields.addressBook')}
      open={addressBookModal}
      className={`w-80 ${isSearchShow ? 'vh-85' : 'h-auto'}`}
      okText={t('translation:default.buttons.add')}
      cancelText={t('translation:default.buttons.cancel')}
      onOk={() => form.submit()}
      onClose={handleAddressBookModalClose}
      onCancel={handleAddressBookModalClose}
      okLoading={saveAddressBookProgressing}
      // biome-ignore lint/complexity/noUselessFragments: placeholder
      customFooter={<></>}
      descriptionClassName="mb-5 d-flex flex-direction-column"
      contentClassName="p-0"
      destroyOnHidden
    >
      <div className="d-flex flex-direction-column flex-1 overflow-hidden">
        <div className="p-15">
          <AddLocationForm
            form={form}
            handleSuccess={onSelectAddressBook}
            handleOnClickSearch={handleOnClickSearch}
            initValues={{ countryCode: defaultSettings?.[0]?.defaultValue }}
          />
        </div>
        {isSearchShow && (
          <>
            <Divider className="m-0 border-top-border" />
            <div className="flex-1 overflow-hidden">
              <div className="py-15 h-100">
                <div className="position-relative h-100 overflow-hidden">
                  <ScrollPagination
                    className="h-100 px-15"
                    loadMore={handleNextPage}
                    isLoading={customerAddressBooks?.progressing}
                    hasMore={
                      customerAddressBooks?.progressing
                        ? false
                        : addressBookFilters.pageNumber <
                          Math.ceil(customerAddressBooks?.totalRecords / addressBookFilters.pageSize)
                    }
                    scrollOffset={50}
                  >
                    {customerAddressBooks?.data?.length > 0
                      ? customerAddressBooks?.data.map((row, index) => (
                          <AddressBookCard
                            key={row?.locationId}
                            index={index}
                            defaultDetails={row}
                            onSelectAddressBook={onSelectAddressBook}
                            customerKey={selectedCompany?.companyId}
                            currentUserId={userId}
                            handleDelete={handleDelete}
                            withSelection
                          />
                        ))
                      : !customerAddressBooks?.progressing &&
                        (customerAddressBooks?.success || customerAddressBooks?.error) && <EmptyRecords />}
                  </ScrollPagination>

                  {(saveAddressBookProgressing || deleteAddressBookProgressing) && (
                    <div className="absolute-spinner">
                      <Spin spinning size="large" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default AddressBookModal;
